import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import cx, { clsx } from 'clsx';
import { useHistory } from 'react-router';

import { Button, HelperTooltip } from '@/components';
import { UserPermissionRes } from '@/hooks';
import { formatNetworth, formatNum } from '@/utils';
import { PostInteractPermission } from '@/views/Stream';

import style from './index.module.scss';
import { useEligibilityBadgeName } from '@/hooks/useEligibilityBadgeName';
import { useGroupDetail } from '@/views/Group/hooks';
import { Link } from 'react-router-dom';

export const InteractPermissions = ({
  postRequiredPermissions,
  userSelfPermissions,
  subTitle,
  withOperation,
  itemClassName,
  type,
}: {
  postRequiredPermissions: PostInteractPermission;
  userSelfPermissions?: UserPermissionRes;
  subTitle?: string;
  itemClassName?: string;
  withOperation?: boolean;
  type?: 'luckDraw' | 'poll';
}) => {
  const { t } = useTranslation();

  const history = useHistory();
  const badgeName = useEligibilityBadgeName({
    badgeEligibility: postRequiredPermissions?.has_one_of_badges,
  });

  const { data: groupDetail } = useGroupDetail(postRequiredPermissions?.is_group_member);

  const handleMintIdClick = useCallback(() => {
    history.push('/settings');
  }, [history]);

  const Eligiblities = useMemo(() => {
    return [
      {
        label: t('InteractPermissions.min_net_worth', {
          amount: formatNetworth(postRequiredPermissions.min_net_worth),
        }),
        key: 'min_net_worth',
        needed: 'min_net_worth' in postRequiredPermissions,
        qualified:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.min_net_worth,
      },
      {
        label: t('InteractPermissions.min_age_days', {
          amount: postRequiredPermissions.min_age_days,
        }),
        key: 'min_age_days',
        needed: 'min_age_days' in postRequiredPermissions,
        qualified:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.min_age_days,
      },
      {
        label: t('InteractPermissions.min_tvf', {
          amount: formatNetworth(postRequiredPermissions.min_tvf),
        }),
        key: 'min_tvf',
        needed: 'min_tvf' in postRequiredPermissions,
        qualified: userSelfPermissions?.has_permission || userSelfPermissions?.details?.min_tvf,
      },
      {
        label: t('InteractPermissions.min_follower_count', {
          amount: formatNum(postRequiredPermissions.min_follower_count),
        }),
        key: 'min_follower_count',
        needed: 'min_follower_count' in postRequiredPermissions,
        qualified:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.min_follower_count,
      },
      {
        label: t('InteractPermissions.min_ranking', {
          amount: formatNum(postRequiredPermissions.min_ranking),
        }),
        key: 'min_ranking',
        needed: 'min_ranking' in postRequiredPermissions,
        qualified: userSelfPermissions?.has_permission || userSelfPermissions?.details?.min_ranking,
      },
      {
        label:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.has_web3_id ? (
            t('InteractPermissions.p9jkw2')
          ) : (
            <Trans i18nKey="InteractPermissions.noWeb3Id">
              Must be a holder of{' '}
              <HelperTooltip
                title={
                  <Trans
                    i18nKey="InteractPermissions.noWeb3IdTip"
                    context={type === 'luckDraw' ? 'luckDraw' : 'poll'}>
                    <Link className={clsx(style.link, style.tip)} to="/settings">
                      Mint a Web3 ID
                    </Link>{' '}
                    to join {type === 'luckDraw' ? 'Lucky Draw' : 'poll'}.
                  </Trans>
                }>
                <Link className={style.link} to="/settings">
                  Web3 ID
                </Link>
              </HelperTooltip>
            </Trans>
          ),
        key: 'has_web3_id',
        needed: 'has_web3_id' in postRequiredPermissions,
        qualified: userSelfPermissions?.has_permission || userSelfPermissions?.details?.has_web3_id,
        operation: (
          <Button type="primary" className={style.mintId} onClick={handleMintIdClick}>
            {t('InteractPermissions.dlpqke')}
          </Button>
        ),
      },
      {
        label: t('InteractPermissions.has_one_of_badges', { badgeName }),
        key: 'has_one_of_badges',
        needed: 'has_one_of_badges' in postRequiredPermissions,
        qualified:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.has_one_of_badges,
      },
      {
        label: t('InteractPermissions.is_group_member', { name: groupDetail?.name }),
        key: 'is_group_member',
        needed: 'is_group_member' in postRequiredPermissions,
        qualified:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.is_group_member,
      },
      {
        label: t('InteractPermissions.tfwez5'),
        key: 'has_followed_user',
        needed: 'has_followed_user' in postRequiredPermissions,
        qualified:
          userSelfPermissions?.has_permission || userSelfPermissions?.details?.has_followed_user,
      },
    ].filter(x => x.needed);
  }, [
    t,
    postRequiredPermissions,
    userSelfPermissions?.has_permission,
    userSelfPermissions?.details?.min_net_worth,
    userSelfPermissions?.details?.min_age_days,
    userSelfPermissions?.details?.min_tvf,
    userSelfPermissions?.details?.min_follower_count,
    userSelfPermissions?.details?.min_ranking,
    userSelfPermissions?.details?.has_web3_id,
    userSelfPermissions?.details?.has_one_of_badges,
    userSelfPermissions?.details?.is_group_member,
    userSelfPermissions?.details?.has_followed_user,
    type,
    handleMintIdClick,
    badgeName,
    groupDetail?.name,
  ]);

  return (
    <>
      <div className={style.subTitle}>{subTitle}</div>
      <div className={style.eligibilities}>
        {Eligiblities.length ? (
          Eligiblities.map(x => (
            <div
              key={x.key}
              className={cx(
                style.eligibility,
                !!userSelfPermissions && !x.qualified && style.inValid,
                withOperation && style.withOperation,
                itemClassName,
              )}>
              <span className={style.circle} />
              <span className={style.eligiLable}>{x.label}</span>
              {!userSelfPermissions || x.qualified ? null : withOperation && x.operation ? (
                x.operation
              ) : (
                <div className={style.inValidTag}>{t('InteractPermissions.1f5nsz3')}</div>
              )}
            </div>
          ))
        ) : (
          <span className={style.eligibility}>
            <span className={style.eligiLable}>{t('InteractPermissions.1n92619')}</span>
          </span>
        )}
      </div>
    </>
  );
};
